import React, {
    useRef,
    useEffect,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
    filter,
    first,
} from 'rxjs/operators';
import _isEqual from 'lodash/isEqual';

import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import MediaPlaybackWrapper from '@mlb/react-patterns/components/MediaPlayback/mediaPlaybackWrapper.tsx';
import COLORS from '@mlb/react-patterns/style/universal.color';

import { normalizeMediaItem } from 'bdvp-common/src/utils';

const PlaybackWrapper = styled(MediaPlaybackWrapper)`
    background-color: ${COLORS.mlbGrey7};
    margin: 0 -18px;

    ${media.mdmin`
        width: 100%;
        margin: 0;
        height: 100%;

        .media-player {
            padding-bottom: unset;
            height: 100%;
            margin: auto;
        }
    `}

    video,
    .mlb-player-controls-loader--container,
    .mlb-player-controls--loader::after {
        background-color: ${COLORS.mlbGrey7} !important;
    }

    .overlay {
        display: none;
    }
`;

const Player = (props) => {

    const {
        config,
        mediaPlayback,
        setMediaPlayer,
        setPlayhead,
        startTime,
        endTime,
        setPlayerState,
        wasPlaying,
        playerState,
        deviceInfo,
        playhead,
        segments,
        roundedSegments,
        getRoundedSegment,
        isSliding,
    } = props;

    const playerConfig = config.player || {};
    const isIos = deviceInfo?.details?.ios;
    const subscriptions = useRef([]);
    const playerStateRef = useRef(playerState);
    const startTimeRef = useRef(startTime);
    const endTimeRef = useRef(endTime);
    const wasPlayingRef = useRef(wasPlaying);
    const playheadRef = useRef(playhead);

    useEffect(() => {

        return (() => {

            subscriptions.current.forEach((subscription) => {

                subscription.unsubscribe();

            });

        });

    }, []);

    useEffect(() => {

        playerStateRef.current = playerState;

    }, [playerState]);

    useEffect(() => {

        startTimeRef.current = startTime;

    }, [startTime]);

    useEffect(() => {

        endTimeRef.current = endTime;

    }, [endTime]);

    useEffect(() => {

        wasPlayingRef.current = wasPlaying;

    }, [wasPlaying]);

    useEffect(() => {

        playheadRef.current = playhead;

    }, [playhead]);

    const onPlayerReady = useCallback((data) => {

        const mediaPlayer = data.mediaPlayer;

        // Remove tabIndex from the video player wrapper
        const videoWrapperEl = mediaPlayer.parentEl;
        if (videoWrapperEl) {

            videoWrapperEl.tabIndex = -1;

        }

        mediaPlayer.seek(startTimeRef.current);
        setMediaPlayer(mediaPlayer);

    }, [setMediaPlayer]);

    return (
        <PlaybackWrapper
            playbackContainerId={'reel-clip-trimming-player'}
            autoPlay={false}
            initConfig={playerConfig.initConfig || {}}
            mediaConfig={{
                ...playerConfig.mediaConfig || {},
                playerTypeConfig: {
                    ...playerConfig.mediaConfig?.playerTypeConfig || {},
                    controls: false,
                },
                controlsConfig: {
                    ...playerConfig.mediaConfig?.controlsConfig || {},
                    showAdControls: false,
                    showDesktopControls: false,
                },
            }}
            playerOptions={{
                ...playerConfig.playerOptions || {},
                metaData: normalizeMediaItem(mediaPlayback),
            }}
            handleObservable={(observationCenter) => {

                subscriptions.current.push(

                    observationCenter.playerSubject.pipe(filter((event) => {

                        return event.type === 'ended';

                    })).subscribe((data) => {

                        data.event.target.currentTime = segments[roundedSegments.indexOf(startTimeRef.current)];
                        if (wasPlayingRef.current) {

                            data.event.target.play();

                        }

                    })
                );

                subscriptions.current.push(
                    observationCenter.playerSubject.pipe(
                        filter((event) => {

                            return isIos && (event.type === 'MEDIA_ITEM_SET' || event.type === 'MEDIA_PLAYER_INITIALIZED');

                        }),
                        first((event) => {

                            return !!event.mediaPlayer;

                        })
                    ).subscribe(onPlayerReady)
                );

                subscriptions.current.push(

                    observationCenter.playerSubject.pipe(
                        filter((event) => {

                            return !isIos && event.type === 'MEDIA_PLAYER_INITIALIZED' && event.mediaPlayer.playerTypeConfig.playerType === 'HLSJS';

                        })
                    ).subscribe(onPlayerReady)
                );

                // update player's current time on play in case playing video fails
                subscriptions.current.push(
                    observationCenter.playerSubject.pipe(
                        filter((event) => {

                            return event.type === 'play';

                        })
                    ).subscribe((data) => {

                        data.event.target.currentTime = segments[roundedSegments.indexOf(playheadRef.current)] || playheadRef.current;

                    })
                );


                // hlsManifestParsed does not trigger on ios
                subscriptions.current.push(
                    observationCenter.playerSubject.pipe(filter((event) => {

                        return !isIos && event.type === 'hlsManifestParsed';

                    })).subscribe((data) => {

                        data.mediaPlayer.hls.startLoad(startTimeRef.current);

                    })
                );

                subscriptions.current.push(
                    observationCenter.playerSubject.pipe(
                        filter((event) => event.type === 'timeupdate')
                    ).subscribe((data) => {

                        let currentTime = data.event.target.currentTime;

                        if(currentTime === 0) {

                            return;

                        }

                        const roundedTime = getRoundedSegment(currentTime);

                        if (
                            roundedTime < startTimeRef.current
                            || roundedTime >= endTimeRef.current
                        ) {

                            currentTime = segments[roundedSegments.indexOf(startTimeRef.current)];
                            data.event.target.currentTime = currentTime;

                        }

                        if (!isSliding.current) {

                            setPlayhead(currentTime);

                        }

                    })
                );

                subscriptions.current.push(
                    observationCenter.stateSubject.subscribe((data) => {

                        if (data.type && data.type !== 'empty' && data.stateModel) {

                            const updatedPlayerState = {
                                paused: data.stateModel.paused,
                                volume: data.stateModel.volume.value,
                                muted: data.stateModel.volume.muted,
                                lastVolume: data.stateModel.volume.lastValue,
                            };

                            if(!_isEqual(playerStateRef.current, updatedPlayerState)) {

                                setPlayerState(updatedPlayerState);

                            }

                        };

                    })
                );

            }}
        />
    );

};

Player.propTypes = {
    config: PropTypes.object.isRequired,
    mediaPlayback: PropTypes.object.isRequired,
    setPlayhead: PropTypes.func.isRequired,
    setPlayerState: PropTypes.func.isRequired,
    startTime: PropTypes.number.isRequired,
    endTime: PropTypes.number.isRequired,
    wasPlaying: PropTypes.bool.isRequired,
    isSliding: PropTypes.shape({
        current: PropTypes.bool.isRequired,
    }).isRequired,
    setMediaPlayer: PropTypes.func.isRequired,
    playerState: PropTypes.shape({
        paused: PropTypes.bool.isRequired,
        volume: PropTypes.number.isRequired,
        muted: PropTypes.bool.isRequired,
        lastVolume: PropTypes.number.isRequired,
    }).isRequired,
    playhead: PropTypes.number.isRequired,
    deviceInfo: PropTypes.object,
};

Player.defaultProps = {
    deviceInfo: {},
};

export default Player;
